import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import {
  CreditBalance,
  CreditTransaction,
  PaginatedTransactions,
  fetchUserCreditTransactions,
} from '../../services/api/creditService';

interface CreditUsageModalProps {
  isOpen: boolean;
  onClose: () => void;
  creditData: CreditBalance | null;
}

const WALLET_TYPE_LABELS: Record<string, string> = {
  monthly: '月間',
  free: '無料',
  additional: '追加',
};

const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  grant: '付与',
  consume: '消費',
  reset: 'リセット',
  expire: '期限切れ',
};

/* 旧 styled-components 定義をクラス文字列定数として保持。
 * 旧 `@keyframes slideIn`（opacity 0 / translateY(10px) → 1 / 0）は
 * globals.css の `msc-slide-up` と同内容なので再利用する。 */
const OVERLAY =
  'fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center ' +
  'z-[10000] p-5 backdrop-blur-[2px]';

const MODAL_CONTAINER =
  'bg-white rounded-tasky-lg w-full max-w-[680px] max-h-[85vh] ' +
  'shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1)] flex flex-col overflow-hidden ' +
  'animate-[msc-slide-up_0.2s_ease-out]';

const MODAL_HEADER = 'flex justify-between items-center px-6 py-5 border-b border-b-gray-200 shrink-0'; /* #E5E7EB */

const MODAL_TITLE = 'text-[18px] font-semibold text-gray-900 m-0'; /* #111827 */

const CLOSE_BUTTON =
  'bg-transparent border-0 p-1 cursor-pointer text-gray-500 rounded-tasky-xs flex ' +
  'items-center justify-center transition-all duration-150 hover:bg-gray-100 hover:text-gray-900'; /* #6B7280 / #F3F4F6 */

const MODAL_BODY = 'flex-1 min-h-0 overflow-y-auto pt-4 px-6 pb-6 flex flex-col gap-4';

const CREDIT_SUMMARY = 'bg-gray-50 border border-gray-200 rounded-tasky-md p-4'; /* #F9FAFB */

const SUMMARY_ROW = 'flex justify-between items-center mb-2';

const SUMMARY_LABEL = 'text-[14px] font-semibold text-gray-900';

const SUMMARY_VALUE = 'text-[20px] font-bold text-gray-900';

const SUMMARY_DETAILS = 'flex gap-4 border-t border-t-gray-200 pt-2';

const SUMMARY_DETAIL =
  'flex flex-col gap-0.5 text-[12px] text-gray-500 ' +
  '[&_span:last-child]:font-medium [&_span:last-child]:text-gray-700'; /* #374151 */

const TABLE_CONTAINER =
  'border border-gray-200 rounded-tasky-md overflow-hidden flex-1 min-h-[200px] flex flex-col';

const TABLE_BODY = 'flex-1 overflow-y-auto';

const TABLE_ROW =
  'flex flex-col gap-1 px-5 py-3 text-[13px] text-gray-900 ' +
  'transition-[background] duration-100 hover:bg-gray-50 ' +
  '[&:not(:last-child)]:border-b [&:not(:last-child)]:border-b-gray-100'; /* #F3F4F6 */

const ROW_MAIN = 'flex items-center justify-between gap-3';

const ROW_DESCRIPTION = 'flex items-center gap-2 min-w-0 flex-1';

const DESCRIPTION_TEXT = 'overflow-hidden text-ellipsis whitespace-nowrap text-[13px] text-gray-900';

const ROW_META = 'flex items-center gap-1.5 pl-0.5';

const META_ITEM = 'text-[12px] text-gray-400'; /* #9CA3AF */

const META_DOT = 'w-[3px] h-[3px] rounded-full bg-gray-300 shrink-0'; /* #D1D5DB */

const WALLET_BADGE_BASE =
  'inline-flex items-center px-1.5 py-px rounded text-[11px] font-medium shrink-0';
const WALLET_BADGE_TYPE: Record<string, string> = {
  monthly: 'bg-blue-100 text-blue-800', // #DBEAFE / #1E40AF
  free: 'bg-emerald-100 text-emerald-800', // #D1FAE5 / #065F46
  additional: 'bg-amber-100 text-amber-800', // #FEF3C7 / #92400E
};
const WALLET_BADGE_DEFAULT = 'bg-gray-100 text-gray-700'; // #F3F4F6 / #374151

const AMOUNT_TEXT_BASE = 'font-semibold text-[14px] shrink-0';
const AMOUNT_TEXT_POSITIVE = 'text-emerald-600'; /* #059669 */
const AMOUNT_TEXT_NEGATIVE = 'text-red-600'; /* #DC2626 */

const LOADING_ROW = 'px-5 py-10 text-center text-gray-500 text-[14px]';

const EMPTY_ROW = 'px-5 py-10 text-center text-gray-400 text-[14px]';

const PAGINATION = 'flex justify-center pt-1';

const PAGINATION_NAV = 'flex items-center gap-3 text-[13px]';

const PAGE_BUTTON =
  'flex items-center gap-1 px-2 py-1 bg-transparent border-0 text-[13px] font-medium ' +
  'transition-[color] duration-150 cursor-pointer text-gray-900 ' +
  'disabled:cursor-not-allowed disabled:text-gray-300 enabled:hover:text-gray-500'; /* #D1D5DB */

const PAGE_NUMBERS = 'flex items-center gap-2';

const PAGE_NUMBER_BASE = 'cursor-pointer hover:text-gray-900';
const PAGE_NUMBER_ACTIVE = 'text-gray-900 font-medium';
const PAGE_NUMBER_INACTIVE = 'text-gray-500 font-normal';

const ELLIPSIS = 'text-gray-400';

const CreditUsageModal: React.FC<CreditUsageModalProps> = ({ isOpen, onClose, creditData }) => {
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const loadTransactions = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const data: PaginatedTransactions = await fetchUserCreditTransactions({
        page,
        per_page: 10,
      });
      setTransactions(data.data);
      setCurrentPage(data.current_page);
      setLastPage(data.last_page);
      setTotal(data.total);
    } catch (e) {
      console.error('Failed to load transactions:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadTransactions(1);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, loadTransactions]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const formatNumber = (n: number) => Math.round(Math.abs(n)).toLocaleString();

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDescription = (tx: CreditTransaction): string => {
    // description 先頭の [pp-bonus:...] は付与の冪等判定用マーカーのため顧客には見せない
    const description = tx.description?.replace(/^\[pp-bonus:[^\]]*\]\s*/, '') || '';
    if (tx.transaction_type === 'grant') {
      const walletLabel = WALLET_TYPE_LABELS[tx.wallet_type] || tx.wallet_type;
      return description || `${walletLabel}クレジット付与`;
    }
    if (tx.transaction_type === 'consume') {
      return description || 'AI使用';
    }
    return description || TRANSACTION_TYPE_LABELS[tx.transaction_type] || tx.transaction_type;
  };

  if (!isOpen) return null;

  return (
    <div className={OVERLAY} onClick={onClose}>
      <div className={MODAL_CONTAINER} onClick={(e) => e.stopPropagation()}>
        <div className={MODAL_HEADER}>
          <h2 className={MODAL_TITLE}>使用状況</h2>
          <button className={CLOSE_BUTTON} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className={MODAL_BODY}>
          {/* Credit summary */}
          {creditData && (
            <div className={CREDIT_SUMMARY}>
              <div className={SUMMARY_ROW}>
                <span className={SUMMARY_LABEL}>クレジット残高</span>
                <span className={SUMMARY_VALUE}>{formatNumber(creditData.total_balance)}</span>
              </div>
              <div className={SUMMARY_DETAILS}>
                <div className={SUMMARY_DETAIL}>
                  <span>無料</span>
                  <span>{formatNumber(creditData.wallets.free.balance)}</span>
                </div>
                <div className={SUMMARY_DETAIL}>
                  <span>月間</span>
                  <span>{formatNumber(creditData.wallets.monthly.balance)} / {formatNumber(creditData.wallets.monthly.limit)}</span>
                </div>
                <div className={SUMMARY_DETAIL}>
                  <span>追加</span>
                  <span>{formatNumber(creditData.wallets.additional.balance)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Transaction list */}
          <div className={TABLE_CONTAINER}>
            <div className={TABLE_BODY}>
              {loading ? (
                <div className={LOADING_ROW}>読み込み中...</div>
              ) : transactions.length === 0 ? (
                <div className={EMPTY_ROW}>取引履歴がありません</div>
              ) : (
                transactions.map((tx) => (
                  <div className={TABLE_ROW} key={tx.id}>
                    <div className={ROW_MAIN}>
                      <div className={ROW_DESCRIPTION}>
                        <span className={`${WALLET_BADGE_BASE} ${WALLET_BADGE_TYPE[tx.wallet_type] ?? WALLET_BADGE_DEFAULT}`}>
                          {WALLET_TYPE_LABELS[tx.wallet_type] || tx.wallet_type}
                        </span>
                        <span className={DESCRIPTION_TEXT}>{getDescription(tx)}</span>
                      </div>
                      <span className={`${AMOUNT_TEXT_BASE} ${tx.amount > 0 ? AMOUNT_TEXT_POSITIVE : AMOUNT_TEXT_NEGATIVE}`}>
                        {tx.amount > 0 ? '+' : '-'}{formatNumber(tx.amount)}
                      </span>
                    </div>
                    <div className={ROW_META}>
                      <span className={META_ITEM}>{tx.user?.name || 'システム'}</span>
                      <span className={META_DOT} />
                      <span className={META_ITEM}>{formatDate(tx.created_at)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Pagination */}
          {lastPage > 1 && (
            <div className={PAGINATION}>
              <div className={PAGINATION_NAV}>
                <button className={PAGE_BUTTON}
                  onClick={() => loadTransactions(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft size={16} />
                  <span>前へ</span>
                </button>

                <div className={PAGE_NUMBERS}>
                  {Array.from({ length: lastPage }, (_, i) => i + 1)
                    .filter(p => Math.abs(p - currentPage) <= 2 || p === 1 || p === lastPage)
                    .map((p, idx, arr) => {
                      const prev = arr[idx - 1];
                      const showEllipsis = prev && p - prev > 1;
                      return (
                        <React.Fragment key={p}>
                          {showEllipsis && <span className={ELLIPSIS}>...</span>}
                          <span
                            className={`${PAGE_NUMBER_BASE} ${p === currentPage ? PAGE_NUMBER_ACTIVE : PAGE_NUMBER_INACTIVE}`}
                            onClick={() => loadTransactions(p)}
                          >
                            {p}
                          </span>
                        </React.Fragment>
                      );
                    })}
                </div>

                <button className={PAGE_BUTTON}
                  onClick={() => loadTransactions(currentPage + 1)}
                  disabled={currentPage >= lastPage}
                >
                  <span>次へ</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditUsageModal;
